import { motion } from 'framer-motion';
import { Timer } from 'lucide-react';
import { cn, formatCountdown, getUrgencyLevel } from '@/lib/utils';
import { useCountdown } from '@/hooks/use-countdown';

interface CountdownBadgeProps {
  expiresAt: string;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  className?: string;
}

const urgencyStyles = {
  calm: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  watch: 'bg-amber-50 text-amber-700 border-amber-200',
  urgent: 'bg-red-50 text-red-700 border-red-200',
  critical: 'bg-red-100 text-red-800 border-red-300',
  // Past timer but the case is still open on GlobeMed — still decidable
  overdue: 'bg-red-100 text-red-800 border-red-300',
};

const sizeStyles = {
  sm: 'text-xs px-2 py-0.5',
  md: 'text-sm px-2.5 py-1',
  lg: 'text-base px-3 py-1.5',
};

export function CountdownBadge({
  expiresAt,
  size = 'md',
  showIcon = true,
  className,
}: CountdownBadgeProps) {
  const secondsLeft = useCountdown(expiresAt);
  const urgency = getUrgencyLevel(secondsLeft);

  return (
    <motion.div
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border font-mono font-semibold tabular-nums',
        urgencyStyles[urgency],
        sizeStyles[size],
        (urgency === 'critical' || urgency === 'overdue') && 'animate-pulse-urgency',
        className,
      )}
      title={urgency === 'overdue'
        ? 'Timer expired — case is still open on GlobeMed; a decision is still possible'
        : undefined}
      layout
    >
      {showIcon && <Timer className={cn(size === 'sm' ? 'w-3 h-3' : size === 'md' ? 'w-3.5 h-3.5' : 'w-4 h-4')} />}
      {formatCountdown(secondsLeft)}
    </motion.div>
  );
}
